/**
 * Judgment rescue / joint-seat tuning + ablation switches.
 * Soft plateau target: deathsMean ≤ 0.25 without combat cheats / Claim B chase.
 *
 * Design anchors (archive 2026-08_판단셀링_외부사례_시사점):
 * - F.E.A.R.: personal survival / Soft≪AA clear outranks squad seed
 * - RTS autocast: assist must be weaker than Soft local optima (crisis-only)
 * - Boids: joint bias only when far from staff (no near-lip puppeteering)
 * - Blackboard commander: seed = suggestion; units keep frame authority
 *
 * Ablate via `setJudgmentAblate("off"|"rescue"|"joint"|"both")` or
 * env JUDGMENT_ABLATE (probe/tests).
 */
export type JudgmentAblateMode =
  | "off"
  | "rescue"
  | "joint"
  | "both"
  | "seat";

export type JudgmentAssistKnobs = {
  /** Min bond for rescue seed bias. */
  rescueBondMin: number;
  /** Max lerp toward cover-subject intercept (≤1). Soft — not full relocate. */
  rescuePullMax: number;
  /** Min Soft-HP crit01 to open rescue (0 = any belowPct hurt). */
  rescueMinCrit01: number;
  /** Kit follow multiplier when rescue active (× pull01). */
  rescueKitFollowMul: number;
  /** Min insight for joint-seat cover bias. */
  jointInsightMin: number;
  /** Max lerp of pack-escape seat toward healer staff. */
  jointPullMax: number;
  /** Min distance to nearest healer before joint bias may fire (px). */
  jointMinDistPx: number;
  /**
   * Dual-seat *filter* score weight (not a pull). Among already-clear seats,
   * Soft-safe survival-seek tip (staff / soak line / anti-sandwich).
   * Soft≪AA clear terms must dominate.
   */
  seatFilterScore: number;
  /**
   * Insight blend toward predicted ally approach seed (0..1).
   * Knob override; else `rolePolicy.survivalSeekPredictBlendMax`.
   */
  predictBlendMax?: number;
};

/**
 * Soft-safe ship defaults (external: RTS autocast weaker than Soft optima).
 * Survival-seek filter ON (score tip only); joint **pull** stays 0.
 * Unit tests may raise knobs temporarily to prove sell causality.
 */
export const JUDGMENT_ASSIST_DEFAULTS: JudgmentAssistKnobs = {
  rescueBondMin: 80,
  rescuePullMax: 0.06,
  rescueMinCrit01: 0.65,
  /** Kit follow add during rescue — 0 keeps Soft formation (FEAR: local authority). */
  rescueKitFollowMul: 0,
  /** Continuous insight tip — no hard 80 gate for Soft ship play case. */
  jointInsightMin: 0,
  /** 0 = joint pull off (Soft regress / Claim-B-adjacent). Filter path only. */
  jointPullMax: 0,
  jointMinDistPx: 160,
  /**
   * Soft-safe tip weight. Clear still wins by ~1e6 in pack-escape tournament.
   * Table override: `rolePolicy.survivalSeekSeatFilterScore`.
   */
  seatFilterScore: 10,
  predictBlendMax: 0.55,
};

let knobs: JudgmentAssistKnobs = { ...JUDGMENT_ASSIST_DEFAULTS };
/** Soft plateau: rescue-only. Joint stays opt-in (ablate joint/both) until safer. */
let ablate: JudgmentAblateMode = "rescue";

export function getJudgmentAssistKnobs(): JudgmentAssistKnobs {
  return knobs;
}

export function setJudgmentAssistKnobs(
  partial: Partial<JudgmentAssistKnobs>,
): void {
  knobs = { ...knobs, ...partial };
}

export function resetJudgmentAssistKnobs(): void {
  knobs = { ...JUDGMENT_ASSIST_DEFAULTS };
}

export function getJudgmentAblate(): JudgmentAblateMode {
  return ablate;
}

export function setJudgmentAblate(mode: JudgmentAblateMode): void {
  ablate = mode;
}

export function resetJudgmentAblate(): void {
  ablate = "rescue";
}

/** Parse env once for headless probes. */
export function applyJudgmentAblateFromEnv(): JudgmentAblateMode {
  const raw = (process.env.JUDGMENT_ABLATE ?? "").trim().toLowerCase();
  if (
    raw === "off" ||
    raw === "rescue" ||
    raw === "joint" ||
    raw === "both" ||
    raw === "seat"
  ) {
    ablate = raw;
  }
  return ablate;
}

export function rescueEnabled(): boolean {
  return ablate === "rescue" || ablate === "both";
}

export function jointEnabled(): boolean {
  return ablate === "joint" || ablate === "both";
}

/** Dual-seat staff filter (no teleport/pull) — wall-5 contract. */
export function seatFilterEnabled(): boolean {
  return (
    ablate === "seat" ||
    ablate === "joint" ||
    ablate === "both" ||
    knobs.seatFilterScore > 0
  );
}
